// Root layout route — the persistent shell (auth gate + Sidebar + TopBar +
// drawers/wizards/palette) that used to be the non-renderPage() part of
// src/App.jsx. Page content now renders via <Outlet/>, matched by
// src/routes/$.tsx (the legacy renderPage() switch, unchanged) until each
// module gets its own real file route. See docs/plans/toasty-soaring-pudding.md
// for the full migration plan — this file is Stage 0.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Archive, CheckCircle2, FileText, Phone, PhoneIncoming, Send, Loader2 } from 'lucide-react';
import { createRootRoute, retainSearchParams, Outlet, useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { CommandPalette } from '../components/CommandPalette';
import { PageErrorBoundary } from '../components/PageErrorBoundary';
import { SignInPage, rememberSignInEmail } from '../components/SignInPage';
import { AcceptInvitationPage } from '../components/AcceptInvitationPage';
import { readSessionMarker, writeSessionMarker, clearSessionMarker } from '../lib/sessionMarker';
import { FeedbackModal } from '../components/FeedbackModal';
import { KeyboardShortcutsHelp } from '../components/KeyboardShortcutsHelp';
import { Sidebar } from '../components/layout/Sidebar';
import { TopBar } from '../components/layout/TopBar';
import { toast } from '../lib/buses';
import { loadSession, signOut } from '../lib/backOfficeApi';
import { queryClient } from '../lib/queryClient';
import { useBroadcast, useKeyboardShortcuts } from '../lib/hooks';
import { useCallCenter } from '../lib/callCenter';
import { ActiveCallBar } from '../modules/callcenter/ActiveCallBar';
import { parsePathname, parentOfRoute, pathForRoute } from '../lib/router';
import { NewCampaignModal } from '../modules/comms/NewCampaignModal';
import { OnboardOrganizerModal } from '../modules/organizers/OnboardOrganizerModal';
import { CreateBoxOfficeWizardModal } from '../modules/sales/CreateBoxOfficeWizardModal';
import { NewManualOrderModal } from '../modules/sales/NewManualOrderModal';
import { Button } from '../ui/Button';
import { StatusChip } from '../ui/Chip';
import { Drawer, DrawerActions, DrawerSection } from '../ui/Drawer';
import { KV } from '../ui/KV';
import { Timeline } from '../ui/Timeline';
import { ToastContainer } from '../ui/Toast';
import { ShellContext } from '../lib/shellContext';
import { useGlobalEventFilter } from '../lib/useGlobalEventFilter';

// `event` is the GLOBAL EVENT LENS — app-level scope applied by 14 surfaces (orders,
// transactions, payouts, wallets, funding, channels, reports, accreditation,
// access-control, audit, design, sales-setup, subscriptions, ticketing-inventory).
// retainSearchParams keeps it across every navigate() without each call site
// having to remember it.
export const Route = createRootRoute({
  component: RootShell,
  search: { middlewares: [retainSearchParams(['event'])] },
});

function RootShell() {
  const navigate = useNavigate();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { active } = parsePathname(pathname);
  // Optional `search` so a destination can name a TAB, not just a page. Without it, ⌘K could
  // only ever land you on a page's default tab — fine for a page, useless for the two surfaces
  // that live as tabs inside one (data-subject requests, organizer KYC).
  const setActive = (a: string, search?: Record<string, unknown>) => navigate({ to: pathForRoute(a, null), ...(search ? { search } : {}) } as any);
  // WHICH SIDEBAR ITEM IS LIT. A detail route has no nav entry of its own — the sidebar matches on
  // `active === item.id`, so on /orders/:id, /transactions/:id, /split-payments/:id and every other
  // detail page NOTHING was highlighted and the operator lost their place in the navigation.
  // `parentOfRoute` already encodes the list a detail belongs to (it is what the not-found state
  // uses to offer the way back), so resolving through it fixes every detail route at once rather
  // than one module at a time.
  const navActive = active ? (parentOfRoute(active) || active) : null;

  // ACCOUNTS, not the buyer aggregate. Opening /people on 'customers' meant a full GROUP BY of
  // the Order table on first paint (375,703 orders → 137,180 contacts, ~19s of aggregation) to
  // list a population nobody browses. See the note in userRoutes' /customers handler.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // 'workspace': a page that fills the content area itself (the event inventory frame), so no padded column.
  const [contentMode, setContentMode] = useState<'default' | 'workspace'>('default');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [drawer, setDrawer] = useState(null); // { type, data }
  const [dashboardChrome, setDashboardChrome] = useState(null);
  const [backendStatus, setBackendStatus] = useState({ state: 'loading', label: 'Connecting to backend', detail: '127.0.0.1:3001' });
  // Mounted once at the app root (not inside the Call Center page) so the live
  // WebSocket connection survives navigation — a call ringing while the
  // operator is on an order shouldn't just vanish because they weren't on
  // this one specific page.
  const callCenter = useCallCenter();

  // THE EVENT LENS LIVES IN THE URL — see useGlobalEventFilter for the hydrate/
  // write-back logic (identical to what used to live inline in App.jsx).
  const [globalEventFilter, setGlobalEventFilter, eventLookup] = useGlobalEventFilter();

  // --- Auth gate: real session required before the app loads any data -------
  // OPTIMISTIC BOOT. If this browser was signed in last time, render the shell NOW and let the
  // real answer land in the background — see lib/sessionMarker for why that is safe and what it
  // replaces. 'checking' (a blank, blocking screen) is now only for a browser that has never
  // signed in here, where there is genuinely nothing to show yet.
  const marker = readSessionMarker();
  const [authState, setAuthState] = useState(marker ? 'authed' : 'checking'); // checking | authed | unauthed
  const [sessionUser, setSessionUser] = useState(marker ? { email: marker.email, name: marker.name } : null);
  // A file-route's `loader` fires the instant the router matches the URL — a router-level event,
  // completely independent of this component's own authState render-gate below. A cold deep link
  // opened while genuinely logged out (e.g. /events/:id?tab=seating) races the loader against
  // sign-in: the loader's fetch 401s, its catch swallows that to null, and BOTH the router's own
  // loader cache and React Query's queryClient (resolveRouteEntity's ensureQueryData) cache that
  // null as if it were a real, resolved answer. Logging in afterwards changes THIS component's
  // authState, but nothing tells the router or query client "that null was wrong, ask again" —
  // no navigation happens (the URL never changed), so the stale cache just sits there and the
  // page reads "not found" forever. One rule closes the whole class for every route, not just
  // Events: the moment a sign-in actually succeeds (authState really was 'unauthed', not the
  // normal cold-start 'checking'), wipe both caches so the CURRENT route re-resolves for real.
  const authStateRef = useRef(authState);
  useEffect(() => { authStateRef.current = authState; }, [authState]);
  const refreshSession = useCallback(async () => {
    const justSignedIn = authStateRef.current === 'unauthed';
    const s = await loadSession();

    // NOBODY ANSWERED — so nothing has been learned, and nothing changes. An unreachable backend
    // is not a signed-out operator: treating it as one logs someone out mid-task over a dropped
    // connection or a backend restart, and (worse, once boot is optimistic) throws away the
    // marker so the next load is slow again for a reason that had nothing to do with them.
    // If we booted optimistically we stay in the shell and let individual calls fail visibly,
    // which is honest — the data is what is missing, not the identity. If we had no marker there
    // is genuinely nothing to show, so the gate stands, but the marker is still not touched.
    if (s.status === 'unknown') {
      if (authStateRef.current === 'checking') setAuthState('unauthed');
      return s;
    }

    setSessionUser(s.user || null);
    if (s.user?.email) rememberSignInEmail(s.user.email);
    // The authority has spoken: refresh the marker, or forget it so the next boot does not
    // optimistically re-enter a session that is gone.
    if (s.status === 'authed') writeSessionMarker(s.user); else clearSessionMarker();
    setAuthState(s.status === 'authed' ? 'authed' : 'unauthed');
    if (s.status === 'authed' && justSignedIn) {
      queryClient.clear();
      router.invalidate();
    }
    return s;
  }, [router]);
  useEffect(() => { refreshSession(); }, [refreshSession]);
  const handleSignOut = async () => { await signOut(); clearSessionMarker(); setSessionUser(null); setAuthState('unauthed'); toast('Signed out', 'info'); };

  // A successful session means the backend is reachable — that's all the shell
  // status needs. We do NOT prefetch the whole dashboard (orders, payouts,
  // requests, finance, health) here: that fired on every route and duplicated
  // DashboardPage's own load. DashboardPage loads its data when you open it.
  useEffect(() => {
    if (authState !== 'authed') return;
    setBackendStatus({ state: 'live', label: 'Backend live', detail: 'Session active' });
  }, [authState]);

  const openDrawer  = (type, data) => setDrawer({ type, data });
  const closeDrawer = () => setDrawer(null);
  const backendGapCount = Object.keys(dashboardChrome?.source?.errors || {}).length;
  const sidebarStatus = dashboardChrome
    ? {
        label: backendGapCount ? `${backendGapCount} data gap${backendGapCount === 1 ? '' : 's'}` : 'Live data',
        tone: backendGapCount ? 'amber' : 'green',
      }
    : {
        label: backendStatus.state === 'loading' ? 'Loading data' : backendStatus.state === 'unavailable' ? 'Data unavailable' : 'Live data',
        tone: backendStatus.state === 'unavailable' ? 'red' : backendStatus.state === 'live' ? 'green' : 'amber',
      };

  // Global quick-action wizard router — wired from TopBar Quick Actions popover
  const [quickWizard, setQuickWizard] = useState(null);
  const { openBroadcast: openQuickBroadcast, broadcastNode: quickBroadcastNode } = useBroadcast();
  const onQuickAction = (id) => {
    if (id === 'manual-order')        { setQuickWizard('manual-order'); }
    else if (id === 'box-office')     { setQuickWizard('box-office'); }
    else if (id === 'organizer')      setQuickWizard('organizer');
    else if (id === 'event')          navigate({ to: '/events', search: { new: 'open' } });
    else if (id === 'accreditation')  { navigate({ to: '/people', search: { tab: 'accreditation' } }); toast('Issue accreditation', 'info', 'Pick an event, then add accredited people'); }
    else if (id === 'campaign')       { setQuickWizard('campaign'); }
    // The bank file is built from the APPROVED payouts — the Payouts page has them, so it opens there.
    else if (id === 'bank-file')      navigate({ to: '/payouts', search: { tab: 'processing', bankFile: 'open' } });
    else if (id === 'broadcast')      { setActive('events'); toast('Open an event to compose a broadcast', 'info'); }
    // Exports are made where their data is (every list's own Export) and kept in the Export center — not by a modal
    // that sized a mock dataset ("20 rows") and exported nothing.
    else if (id === 'export')         navigate({ to: '/reports', search: { tab: 'exports' } });
    // Navigations, not wizards — and deliberately no toast. The previous navigate-and-apologise
    // entries ('event', 'broadcast') tell you where to click next; these land you ON the thing.
    else if (id === 'data-requests')  navigate({ to: '/risk', search: { tab: 'Data requests' } });
    else if (id === 'organizer-kyc')  navigate({ to: '/risk', search: { tab: 'KYC & compliance' } });
  };

  // Every branch here OPENS SOMETHING — see original App.jsx doc comment history
  // for why the apology-toast/no-op variants were removed.
  const handlePaletteAction = (action) => {
    if (action === 'new-event')                navigate({ to: '/events', search: { new: 'open' } });
    else if (action === 'new-manual-order')    setQuickWizard('manual-order');
    else if (action === 'onboard-organizer')   setQuickWizard('organizer');
    else if (action === 'new-box-office')      setQuickWizard('box-office');
    else if (action === 'issue-accreditation') setQuickWizard('accreditation');
    else if (action === 'send-campaign')       setQuickWizard('campaign');
    else if (action === 'show-help')           setShortcutsHelpOpen(true);
    else if (action === 'needs-help')          navigate({ to: pathForRoute('support', null), search: { tab: 'needs-help' } });
  };

  useKeyboardShortcuts({ setActive, setPaletteOpen, setHelpOpen: setShortcutsHelpOpen, openPaletteAction: handlePaletteAction });

  // Keyboard shortcut: ⌘. or Ctrl+. opens Quick Actions popover (focusable button)
  useEffect(() => {
    const onKey = (e) => {
      const inField = e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable);
      if (inField) return;
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        const btn = document.querySelector('[data-quick-actions-trigger="true"]');
        if (btn) btn.click();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ACCEPTING AN INVITATION happens outside the gate and the shell: the person is not staff until
  // they accept, and the page does its own sign-in (components/AcceptInvitationPage). After every hook.
  if (pathname === '/accept-invitation') {
    return (
      <>
        <AcceptInvitationPage />
        <ToastContainer />
      </>
    );
  }
  if (authState === 'checking') {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#FBFAF7] text-[#6B7280]">
        <div className="flex items-center gap-2 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" />Checking session…</div>
      </div>
    );
  }
  if (authState === 'unauthed') {
    return <SignInPage onSignedIn={refreshSession} />;
  }

  const shellValue = {
    authState, sessionUser, callCenter,
    openDrawer, closeDrawer,
    dashboardChrome, setDashboardChrome,
    backendStatus,
    quickWizard, setQuickWizard, onQuickAction,
    globalEventFilter, setGlobalEventFilter,
    // Where the lookup of ?event= stands (lib/useGlobalEventFilter): loading, or failed with why and a retry.
    eventLookup,
    // A wide working surface (the event seat map) collapses the sidebar to its rail while it is open.
    sidebarCollapsed, setSidebarCollapsed,
    contentMode, setContentMode,
  };

  // A full-screen tool (the Venue Studio) owns the whole window: no sidebar, top bar or padded
  // column. Auth, the shell context and toasts still wrap it.
  if (active === 'venue-studio' || active === 'venue-canvas') {
    return (
      <ShellContext.Provider value={shellValue}>
        <PageErrorBoundary resetKey={active}>
          <Outlet />
        </PageErrorBoundary>
        <ToastContainer />
      </ShellContext.Provider>
    );
  }

  return (
    <ShellContext.Provider value={shellValue}>
      <div className="flex bg-[#F7F6F2] min-h-screen text-[#1F2937]" style={{ fontFamily: "var(--brand-font, 'Inter'), 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
        <Sidebar active={navActive} onSelect={(id) => { setActive(id); }} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} statusLabel={sidebarStatus.label} statusTone={sidebarStatus.tone} onFeedback={() => setFeedbackOpen(true)} liveBadges={{ 'call-center': callCenter.ringingCalls.length > 0 ? 'live' : undefined }} userEmail={sessionUser?.email} />
        <div className="flex-1 min-w-0 flex flex-col">
          <TopBar onOpenPalette={() => setPaletteOpen(true)} onQuickAction={onQuickAction} onNavigate={setActive} backendStatus={backendStatus} dashboard={dashboardChrome} sessionUser={sessionUser} onSignOut={handleSignOut} />
          {/* Quick Setup (src/modules/venue-port) is a workspace page: it fills the content area below the
              top bar and scrolls inside itself, so the padded, max-width column would inset it and make
              the page scroll twice. */}
          <main className={active === 'venue-new' || active === 'venue-setup' || contentMode === 'workspace' ? 'flex-1 min-w-0 w-full' : 'flex-1 px-6 py-5 max-w-[1600px] w-full mx-auto'}>
            <PageErrorBoundary resetKey={active}>
              <Outlet />
            </PageErrorBoundary>
          </main>
        </div>

        {/* Global quick-action wizards */}
        {quickWizard === 'manual-order' && <NewManualOrderModal onClose={() => setQuickWizard(null)} />}
        {quickWizard === 'box-office'    && <CreateBoxOfficeWizardModal onClose={() => setQuickWizard(null)} />}
        {quickWizard === 'organizer'     && <OnboardOrganizerModal onClose={() => setQuickWizard(null)} />}
        {/* No 'accreditation' wizard. The quick action navigates straight to the real panel
            (/people?tab=accreditation) — see onQuickAction above — so `setQuickWizard('accreditation')`
            is never called and the IssueAccreditationModal mounted here could not render. It was a
            parallel MOCK of a subsystem that exists for real: AccreditationPanel issues through
            `createPassHolder` against /api/pass-holders, requires a real per-event accreditation
            category, and lists what it created. The wizard invented a `PRS-####` id from
            Math.random, picked events out of MOCK, and toasted 'Accreditation issued' twice — here
            and inside itself — before landing the operator on the real panel, where the person they
            had just been told was accredited did not appear. */}
        {/* The modal saves a DRAFT and says so itself; nothing is sent from here, so nothing else is announced. */}
        {quickWizard === 'campaign'      && <NewCampaignModal onClose={() => setQuickWizard(null)} onSend={() => setQuickWizard(null)} />}
        {quickBroadcastNode}

        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          onNavigate={(id: string, search?: Record<string, unknown>) => setActive(id, search)}
          onAction={handlePaletteAction}
          userEmail={sessionUser?.email}
        />
        <KeyboardShortcutsHelp open={shortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} />
        <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

        {/* 'user' and 'payout' drawers removed — nothing anywhere called openDrawer('user')
            or openDrawer('payout'), so neither could be opened. Both were also pure fabrication:
            hydrateUser stamped every user with joined "14 Mar 2024", country Uganda, 2 devices
            and an invented activity timeline; hydratePayout gave every payout destination
            "Stanbic Bank · ••4291", approver "Brian O." and a "Compliance check passed ·
            Auto-screened · no flags" entry — an audit trail for a screening that never ran. */}
        {/* The 'risk' drawer is gone with the fabricated case queue that was its only way in.
            Its actions toasted "Payouts frozen" and "Case escalated" against cases that did not
            exist, on the screen where a real operator would believe them. */}
        {drawer?.type === 'template' && (
          <Drawer open onClose={closeDrawer} title={drawer.data.name} subtitle={`${drawer.data.id} · v${drawer.data.version}`} status={drawer.data.status}>
            <DrawerSection title="Template details">
              <div className="grid grid-cols-2 gap-x-4">
                <KV label="Template ID" value={drawer.data.id} />
                <KV label="Type" value={drawer.data.type} />
                <KV label="Channel" value={drawer.data.channel} />
                <KV label="Language" value={drawer.data.lang} />
                <KV label="Version" value={`v${drawer.data.version}`} />
                <KV label="Status" value={<StatusChip status={drawer.data.status} />} />
                <KV label="Linked to" value={drawer.data.linked} />
                <KV label="Total sends" value={drawer.data.sends.toLocaleString()} />
                <KV label="Last edited" value={drawer.data.edited} />
                <KV label="Last editor" value={drawer.data.editor} />
              </div>
            </DrawerSection>
            <DrawerSection title="Preview">
              <div className="border border-[#E6E4E1] rounded-md p-4 bg-[#FBFAF7] text-[12.5px] text-[#374151] leading-relaxed">
                <div className="font-medium text-[#1F2937] mb-1.5">{drawer.data.name}</div>
                Hi {'{{customer_first_name}}'}, your booking for <span className="font-medium">{'{{event_name}}'}</span> on {'{{event_date}}'} is confirmed. Order reference {'{{order_id}}'}.
                <div className="mt-2 text-[#6B7280]">Variables resolve at send time from order & event context.</div>
              </div>
            </DrawerSection>
            <DrawerSection title="Version history">
              <Timeline items={[
                { time: drawer.data.edited, label: `v${drawer.data.version} published`, detail: `By ${drawer.data.editor}`, icon: CheckCircle2, tone: 'green' },
                { time: '2w ago', label: `v${drawer.data.version - 1} archived`, detail: 'Replaced by current version', icon: Archive, tone: 'neutral' },
                { time: '1mo ago', label: 'Template created', detail: 'Initial draft', icon: FileText, tone: 'neutral' },
              ]} />
            </DrawerSection>
            <DrawerActions>
              <Button variant="ghost" size="sm" icon={Send} onClick={() => toast('Test send queued', 'success', `${drawer.data.id} · to your inbox`)}>Send test</Button>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => toast('Template duplicated', 'success', `New: ${drawer.data.id}-copy · live now`)}>Duplicate</Button>
                <Button variant="primary" size="sm" onClick={() => toast('Design Studio opened', 'info', drawer.data.id)}>Edit template</Button>
              </div>
            </DrawerActions>
          </Drawer>
        )}
        {/* A call ringing while the operator is anywhere else in the app must
            still be answerable — the Call Center page itself already shows its
            own ringing card, so this only appears when they're NOT there. */}
        {/* The far end's voice, for every call wherever it was answered or placed — it lived only on the
            Call Center page, so a call taken from the ringing card elsewhere was silent and had no hang-up. */}
        <audio ref={callCenter.attachAudioEl} autoPlay />
        {active !== 'call-center' && <ActiveCallBar callCenter={callCenter} onOpen={() => setActive('call-center')} />}
        {active !== 'call-center' && callCenter.ringingCalls.map((call) => (
          <div key={call.callId} className={`fixed ${callCenter.activeCall ? 'bottom-[136px]' : 'bottom-5'} right-5 z-[95] bg-white border border-[#F0D4A0] rounded-[12px] shadow-xl p-4 w-[320px]`}>
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#F0D4A0] opacity-75 animate-ping" />
                <div className="relative w-10 h-10 rounded-full bg-[#C48319] text-white flex items-center justify-center">
                  <PhoneIncoming className="w-4 h-4" />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold text-[#1F2937] truncate">{call.contactName || call.from}</div>
                <div className="text-[11px] text-[#6B7280]">Incoming WhatsApp call</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" icon={Phone} onClick={() => callCenter.answerCall(call)} className="!bg-[#1F6A3A] !border-[#1F6A3A] hover:!bg-[#175A30] flex-1">Answer</Button>
              <Button variant="secondary" size="sm" onClick={() => callCenter.declineCall(call)}>Decline</Button>
              <Button variant="ghost" size="sm" onClick={() => setActive('call-center')}>Open</Button>
            </div>
          </div>
        ))}

        <ToastContainer />
      </div>
    </ShellContext.Provider>
  );
}
